/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ComponentType} from 'react';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {MaterialExceptionIcon} from '../../../icons/current/MaterialExceptionIcon';
import useIconDLS from '../../../icons/useIconDLS';
import {dlsColor, dlsDarkColor, dlsTextColor} from '../../../theme/theme';
import {IconProps} from '../../../types/types';

/**
 * The severity driving a single `DiagnosticTile`'s visual style, resolved
 * upstream (per the ION-7360 threshold rules) and passed in already
 * decided - `DiagnosticTile` never computes thresholds itself. Distinct
 * from `IndicatorStatus` (`vehicleTypeIndicatorConfig`, the compact
 * Diagnostic Summary Bar's own status type): this ticket's Diagnostic Tile
 * Grid has no `unsupported` state, and its `warning`/`critical` visuals are
 * a wider vocabulary (a bordered tile, not just an icon recolor) than the
 * compact bar's.
 */
export type DiagnosticTileSeverity = 'good' | 'warning' | 'critical';

/**
 * The 9 fields confirmed directly against the Figma "Diagnostics" master
 * component export, in its left-to-right/top-to-bottom order, plus
 * `batteryLevel` - the legacy `AssetDetailsDiagnostics`' own "Battery
 * Level(int/ext)" device/external-power-voltage row, which has no Figma
 * tile of its own but is kept for legacy parity (confirmed missing from
 * this grid during ION-7462 visual QA) and appended after the 9 Figma
 * tiles rather than interleaved among them.
 */
export type DiagnosticTileKey =
  | 'seatbelt'
  | 'engineLight'
  | 'assetBattery'
  | 'fuel'
  | 'charge'
  | 'odometer'
  | 'engineHours'
  | 'gpsQuality'
  | 'deviceBattery'
  | 'batteryLevel';

/**
 * The Diagnostic Global Priority order (Details.txt: "Tiles are placed left
 * to right, then top to bottom ... following the Diagnostic Global Priority
 * order"), derived directly from the Figma "Diagnostics" export's tile
 * grid, with `batteryLevel` appended last (see `DiagnosticTileKey`).
 * Callers assembling a `DiagnosticTileData[]` should sort by this order
 * (after dropping any field the asset has no data for) so
 * `DiagnosticTileGrid`'s reflow matches the Figma "Diagnostic Tile Layout
 * Flow" spec.
 */
export const DIAGNOSTIC_TILE_ORDER: DiagnosticTileKey[] = [
  'seatbelt', 'engineLight', 'assetBattery',
  'fuel', 'charge', 'odometer',
  'engineHours', 'gpsQuality', 'deviceBattery',
  'batteryLevel'
];

/**
 * The uppercase tile label text per field, verified directly against the
 * Figma "Diagnostics" export - callers building `DiagnosticTileData` should
 * pull labels from here rather than re-typing them ad hoc. `batteryLevel`
 * has no Figma export to verify against (see `DiagnosticTileKey`); its
 * label follows the same abbreviated-uppercase convention as the rest.
 */
export const DIAGNOSTIC_TILE_LABELS: Record<DiagnosticTileKey, string> = {
  seatbelt      : 'SEAT BELT',
  engineLight   : 'ENG LIGHT',
  assetBattery  : 'ASSET BATT',
  fuel          : 'FUEL LEVEL',
  charge        : 'VEHICLE CHRG',
  odometer      : 'ODOMETER',
  engineHours   : 'ENGINE HRS',
  gpsQuality    : 'GPS QUALITY',
  deviceBattery : 'DEVICE BATT',
  batteryLevel  : 'BATTERY LVL'
};

/**
 * The view model `DiagnosticTileGrid` renders one `DiagnosticTile` per -
 * assembled by whatever supplies asset diagnostic values, with `severity`
 * already resolved per the ION-7360 thresholds. Fields the asset has no
 * data for are simply omitted from the array (not included with a
 * placeholder), which is what lets the remaining tiles reflow per the
 * Figma "Diagnostic Tile Layout Flow" spec.
 */
export interface DiagnosticTileData {
  id: DiagnosticTileKey;
  label: string;
  value: string;
  unit?: string;
  severity: DiagnosticTileSeverity;
}

/**
 * The resolved visual for a `DiagnosticTile` at a given severity: the
 * tile's own background/border, its label text color (value text is
 * always Grey 2 regardless of severity, so it has no entry here), and the
 * small severity icon shown to the right of the label - `null` for `good`,
 * which shows no icon at all.
 *
 * Deliberately has no per-`DiagnosticTileKey` icon mapping (the way
 * `vehicleTypeIndicatorConfig`'s `HEALTH_INDICATOR_ICONS`/
 * `ALERT_INDICATOR_ICONS` map a field to e.g. `SeatbeltIcon`/
 * `GpsQualityIcon` for the compact Diagnostic Summary Bar). Verified
 * directly against the Figma "Diagnostics" and "Diagnostic Tiles by
 * Severity" dev-mode CSS exports: every tile's "Label & Alert" row
 * contains only the label text at `good`, and adds exactly one *severity*
 * icon (not a field icon) at `warning`/`critical`. There is no per-field
 * icon layer in any tile, at any severity - so `DiagnosticTile` has no
 * `icon` prop, and this config has no per-field icon to reuse from
 * `vehicleTypeIndicatorConfig`. This is also why Odometer/Engine Hours (the
 * 2 fields new to this ticket) need no new icon under `icons/current`: no
 * field in this grid renders a field-specific icon at all.
 */
export interface DiagnosticTileSeverityVisual {
  background: string;
  border: string;
  labelColor: string;
  Icon: ComponentType<IconProps> | null;
  iconColor?: string;
}

/**
 * The Warning-severity overlay glyph - an all-orange (`#E17300`) triangle
 * with an exclamation mark, verified directly against the reference
 * `UrgentIcon.txt` Figma export. Defined locally here rather than added as
 * a new file under `icons/current`, because the repo's existing
 * `UrgentIcon` component hardcodes its outer triangle to Status/Red - built
 * for a different, always-red-bordered use elsewhere - so it can't
 * reproduce this tile's all-orange triangle without a color override its
 * own API doesn't expose.
 */
const DiagnosticTileWarningIcon = (props: IconProps) => {
  const {iconSize, iconColor} = useIconDLS(props);
  return (
    <svg width={iconSize} height={iconSize} viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path d='M9.42976 2.25311C9.23741 1.91896 8.75522 1.91906 8.563 2.25328L1.18093 15.0888C0.989222 15.4221 1.22983 15.8381 1.61436 15.8381H16.3852C16.7699 15.8381 17.0105 15.422 16.8186 15.0886L9.42976 2.25311Z' stroke={iconColor} strokeWidth='1.5' strokeMiterlimit='10' />
      <path d='M8.99463 6.48071V11.2651' stroke={iconColor} strokeWidth='1.5' strokeMiterlimit='10' />
      <path d='M8.99463 12.1271V13.4498' stroke={iconColor} strokeWidth='1.5' strokeMiterlimit='10' />
    </svg>
  );
};

/**
 * Builds the severity-to-visual map fresh on every call (not memoized) so
 * `dlsColor`/`dlsTextColor` are always resolved against the currently
 * active theme - same reasoning as `vehicleTypeIndicatorConfig`'s
 * `makeIndicatorColorResolver`. `#E17300` (the Warning icon color) has no
 * matching theme token (confirmed against `theme-current.test.ts`), so it
 * is hardcoded here in light, same as `vehicleTypeIndicatorConfig`'s own
 * Warning colors - dark mode uses the `orange` token instead.
 */
const buildDiagnosticTileVisuals = (
  isDark: boolean
): Record<DiagnosticTileSeverity, DiagnosticTileSeverityVisual> => {
  const criticalRedRgb = hexToRgb(dlsColor('red')).join(', ');
  return {
    good : {
      background : dlsColor('white'),
      border     : `1px solid ${dlsColor('grey7')}`,
      labelColor : dlsColor('grey3'),
      Icon       : null
    },
    warning : {
      background : dlsColor('white'),
      border     : `1px solid ${dlsColor('grey7')}`,
      labelColor : dlsColor('grey3'),
      Icon       : DiagnosticTileWarningIcon,
      iconColor  : dlsDarkColor(isDark, 'orange', '#E17300')
    },
    critical : {
      background : `rgba(${criticalRedRgb}, 0.05)`,
      border     : `1px solid rgba(${criticalRedRgb}, 0.3)`,
      labelColor : dlsTextColor('red'),
      Icon       : MaterialExceptionIcon,
      iconColor  : dlsColor('red')
    }
  };
};

/**
 * Resolves the `DiagnosticTile` visual for a given severity.
 *
 * ```typescript
 *
 *   import {getDiagnosticTileSeverityVisual} from
 *     '@ion/dls/src/components/assetdetails/current/diagnosticTileConfig';
 *   ...
 *
 *   const visual = getDiagnosticTileSeverityVisual('critical', isDark);
 *
 * ```
 *
 * @param severity - the resolved Diagnostic Tile severity
 * @param isDark   - `true` when dark mode is active, defaults to `false`
 * @returns The tile's background/border/label color and severity icon
 */
export const getDiagnosticTileSeverityVisual = (
  severity: DiagnosticTileSeverity,
  isDark = false
): DiagnosticTileSeverityVisual => buildDiagnosticTileVisuals(isDark)[severity];
